import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
} from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import { history } from "../../Helpers/history";
import { formatAmount } from "../../Utils/formatAmount";
import axios from "axios";
import config from "../../Helpers/config.json";
import { errorMessages } from "../../Helpers/ErrorMessages";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { Alert } from "react-bootstrap";
import CustomDialog from "../../Utils/Dialog/CustomDialog";
import { dateStringConvert } from "../../Utils/titleCaseFunction";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  invoiceCard: {
    marginBottom: theme.spacing(2),
    border: "2px solid #e0e0e0",
    borderRadius: theme.spacing(1),
  },
  originalCard: {
    borderColor: "#4caf50",
  },
  duplicateCard: {
    borderColor: "#ff9800",
  },
  cardHeader: {
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
  },
  originalHeader: {
    backgroundColor: "#e8f5e8",
  },
  duplicateHeader: {
    backgroundColor: "#fff3e0",
  },
  productTable: {
    marginTop: theme.spacing(2),
  },
  mergeButtonContainer: {
    textAlign: "right",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  loadingContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  summaryBox: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: "#f9f9f9",
    marginBottom: theme.spacing(2),
  },
  conflictWarning: {
    marginBottom: theme.spacing(2),
  },
  productDetailsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  productCount: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  expandIcon: {
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  collapsedProductSummary: {
    padding: theme.spacing(1),
    backgroundColor: "#f9f9f9",
    borderRadius: theme.spacing(0.5),
    margin: theme.spacing(1, 0),
  }
}));

const MergeInvoices = ({ actions, snackbars }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [duplicateInvoice, setDuplicateInvoice] = useState(null);
  const [originalDetails, setOriginalDetails] = useState([]);
  const [duplicateDetails, setDuplicateDetails] = useState([]);
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [originalExpanded, setOriginalExpanded] = useState(false);
  const [duplicateExpanded, setDuplicateExpanded] = useState(false);

  // Get route parameters
  const params = history.location.pathname.split('/');
  const originalInvoiceId = params[2];
  const duplicateInvoiceId = params[3];
  const invoiceData = history.location.state?.invoiceData;


  useEffect(() => {
    if (originalInvoiceId && duplicateInvoiceId) {
      fetchInvoiceDetails();
    } else {
      actions.snackbarActions.errorSnackbar("Invalid invoice parameters", "error");
      history.push('/duplicate_invoices');
    }
  }, [originalInvoiceId, duplicateInvoiceId]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      // Fetch both invoices details in parallel
      const [originalResponse, duplicateResponse] = await Promise.all([
        axios.get(`${config.epharmUrl}/Cpurchase/purchase_details_data?purchase_id=${invoiceData.originalPurchaseId}`),
        axios.get(`${config.epharmUrl}/Cpurchase/purchase_details_data?purchase_id=${invoiceData.duplicatePurchaseId}`)
      ]);

      if (originalResponse.data && duplicateResponse.data) {
        setOriginalInvoice(originalResponse.data);
        setDuplicateInvoice(duplicateResponse.data);
        setOriginalDetails(originalResponse.data.purchase_all_data || []);
        setDuplicateDetails(duplicateResponse.data.purchase_all_data || []);
      }
    } catch (err) {
      errorMessages(err, null, actions);
      actions.snackbarActions.errorSnackbar("Failed to fetch invoice details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMergeInvoices = async () => {
    setMerging(true);
    try {
      const response = await axios.post(
        `${config.epharmUrl}/Cpurchase/invoices/merge`,
        {
          target_invoice_id: originalInvoiceId,
          source_invoice_id: duplicateInvoiceId
        }
      );

      if (response.data.status === 'success') {
        actions.snackbarActions.successSnackbar("Invoices merged successfully!", "success");
        history.push('/duplicate_invoices');
      } else {
        actions.snackbarActions.errorSnackbar(response.data.message || "Failed to merge invoices", "error");
      }
    } catch (err) {
      errorMessages(err, null, actions);
      actions.snackbarActions.errorSnackbar("Failed to merge invoices", "error");
    } finally {
      setMerging(false);
      setMergeConfirmOpen(false);
    }
  };

  const handleCloseBar = () => {
    actions.snackbarActions.hideSnackBar();
  };

  const renderInvoiceCard = (invoice, details, isOriginal) => {
    const cardClass = isOriginal ? classes.originalCard : classes.duplicateCard;
    const headerClass = isOriginal ? classes.originalHeader : classes.duplicateHeader;
    const title = isOriginal ? "Original Invoice" : "Duplicate Invoice";
    const isExpanded = isOriginal ? originalExpanded : duplicateExpanded;
    const toggleExpanded = isOriginal 
      ? () => setOriginalExpanded(!originalExpanded)
      : () => setDuplicateExpanded(!duplicateExpanded);
    
    return (
      <Card className={`${classes.invoiceCard} ${cardClass}`}>
        <Box className={`${classes.cardHeader} ${headerClass}`}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <Typography variant="subtitle2">
            Invoice #: {invoice.chalan_no}
          </Typography>
        </Box>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Supplier:</strong> {invoice.manufacturer_name}</Typography>
              <Typography variant="body2"><strong>Purchase Date:</strong> {invoice.purchase_date ? dateStringConvert(invoice.purchase_date) : ''}</Typography>
              {/* <Typography variant="body2"><strong>Purchase ID:</strong> {invoice.purchase_id}</Typography> */}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Total Amount:</strong> {formatAmount(invoice.sub_total_amount)}</Typography>
              <Typography variant="body2"><strong>Products Count:</strong> {details.length}</Typography>
            </Grid>
          </Grid>

          {/* Collapsible Product Details Section */}
          <Box className={classes.productTable}>
            <Box 
              className={classes.productDetailsHeader}
              onClick={toggleExpanded}
            >
              <Typography variant="body1">
                Product Details
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography className={classes.productCount}>
                  {details.length} {details.length === 1 ? 'item' : 'items'}
                </Typography>
                <IconButton size="small" className={classes.expandIcon}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Box>

            {/* Collapsed Summary */}
            {!isExpanded && details.length > 0 && (
              <Box className={classes.collapsedProductSummary}>
                <Typography variant="body2" color="textSecondary">
                  Click above to view {details.length} products in this invoice
                </Typography>
                <Typography variant="body2" style={{ fontSize: '0.75rem', marginTop: 4 }}>
                  Total: {formatAmount(invoice.sub_total_amount)}
                </Typography>
              </Box>
            )}

            {/* Expanded Product Table */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.batch_id}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatAmount(item.manufacturer_price)}</TableCell>
                      <TableCell align="right">{formatAmount(item.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No product details found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Collapse>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const { open, message, variant } = snackbars;

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Loading invoice details...
        </Typography>
      </div>
    );
  }

  if (!originalInvoice || !duplicateInvoice) {
    return (
      <div className={classes.root}>
        <ReusableDashboardHeader
          heading="Purchases"
          subHeading="Merge Invoices - Error"
          component="Merge Invoices"
          link={history.location.pathname}
        />
        <Alert severity="error">
          Failed to load invoice details. Please try again.
        </Alert>
        <Box mt={2}>
          <button
            className="btn pharmacy-gray-btn"
            onClick={() => history.push('/duplicate_invoices')}
          >
            Back to Duplicate Invoices
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ReusableDashboardHeader
        heading="Purchases"
        subHeading="Merge Duplicate Invoices"
        component="Merge Invoices"
        link={history.location.pathname}
      />

      <PharmacySnackbar
        open={open}
        message={message}
        variant={variant}
        handleCloseBar={handleCloseBar}
      />

      <button
          className="btn pharmacy-gray-btn mr-4"
          onClick={() => history.push('/duplicate_invoices')}
          disabled={merging}
        >
          Back to List
        </button>

      {/* Merge Summary */}
      <Box className={classes.summaryBox}>
        <Typography variant="h6" gutterBottom>
          Merge Summary
        </Typography>
        <Typography variant="body2">
          <strong>Action:</strong> Merge "{duplicateInvoice.chalan_no}" into "{originalInvoice.chalan_no}"
        </Typography>
        <Typography variant="body2">
          <strong>Result:</strong> The duplicate invoice will be combined with the original. 
          Duplicate products will be consolidated, and the duplicate invoice will be marked as merged.
        </Typography>
      </Box>

            {/* Action Buttons */}
      <Box className={classes.mergeButtonContainer}>
        
        <button
          className="btn pharmacy-info-btn"
          onClick={() => setMergeConfirmOpen(true)}
          disabled={merging}
          
        >
          {merging ? <CircularProgress size={24} /> : 'Merge Invoices'}
        </button>
      </Box>

      {/* Invoice Details Side by Side */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          {renderInvoiceCard(originalInvoice, originalDetails, true)}
        </Grid>
        <Grid item xs={12} lg={6}>
          {renderInvoiceCard(duplicateInvoice, duplicateDetails, false)}
        </Grid>
      </Grid>


      {/* Confirmation Dialog */}
      <CustomDialog
        open={mergeConfirmOpen}
        handleClose={() => setMergeConfirmOpen(false)}
        title="Confirm Invoice Merge"
        maxWidth="sm"
        buttons={
          <div>
            <button
              className="btn pharmacy-gray-btn mr-3"
              onClick={() => setMergeConfirmOpen(false)}
              disabled={merging}
            >
              Cancel
            </button>
            <button
              onClick={handleMergeInvoices}
              className="btn pharmacy-info-btn"
              disabled={merging}
            >
              {merging ? <CircularProgress size={20} /> : 'Confirm Merge'}
            </button>
          </div>
        }
      >
        <Typography variant="body1" gutterBottom>
          Are you sure you want to merge these invoices?
        </Typography>
        {/* <Typography variant="body2" color="textSecondary">
          This action will:
        </Typography>
        <ul>
          <li>Combine all products from both invoices</li>
          <li>Update quantities for duplicate products</li>
          <li>Mark the duplicate invoice as merged</li>
          <li>This action cannot be undone</li>
        </ul> */}
      </CustomDialog>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    snackbars: state.snackbars,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      snackbarActions: bindActionCreators(snackbarActions, dispatch),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MergeInvoices);